import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  OnInit,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import { DashboardService, getPeriodRange } from '../../services/dashboard.service';
import type {
  DashboardData,
  DashboardPeriod,
  DashboardPeriodType
} from '../../services/dashboard.service';
import { formatDashboardCurrency } from '../dashboard/dashboard-utils';
import { DashKpiCollectionWidgetComponent } from '../dashboard/widgets/dash-kpi-collection.widget';
import { DashKpiProjectionWidgetComponent } from '../dashboard/widgets/dash-kpi-projection.widget';
import { DashKpiPendingWidgetComponent } from '../dashboard/widgets/dash-kpi-pending.widget';
import { DashKpiRateWidgetComponent } from '../dashboard/widgets/dash-kpi-rate.widget';
import { DashSalesChartWidgetComponent } from '../dashboard/widgets/dash-sales-chart.widget';
import { DashCollectionTimelineWidgetComponent } from '../dashboard/widgets/dash-collection-timeline.widget';
import { DashCollectionBreakdownWidgetComponent } from '../dashboard/widgets/dash-collection-breakdown.widget';
import { DashUpcomingOverdueWidgetComponent } from '../dashboard/widgets/dash-upcoming-overdue.widget';
import { DashInvoiceStatusWidgetComponent } from '../dashboard/widgets/dash-invoice-status.widget';
import { DashRecentPaymentsWidgetComponent } from '../dashboard/widgets/dash-recent-payments.widget';

@Component({
  selector: 'app-forecast',
  templateUrl: './forecast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    DashKpiCollectionWidgetComponent,
    DashKpiProjectionWidgetComponent,
    DashKpiPendingWidgetComponent,
    DashKpiRateWidgetComponent,
    DashSalesChartWidgetComponent,
    DashCollectionTimelineWidgetComponent,
    DashCollectionBreakdownWidgetComponent,
    DashUpcomingOverdueWidgetComponent,
    DashInvoiceStatusWidgetComponent,
    DashRecentPaymentsWidgetComponent
  ]
})
export class ForecastComponent implements OnInit {
  private dashboardService = inject(DashboardService);

  constructor() {
    Chart.register(...registerables);
  }

  data = signal<DashboardData | null>(null);
  period = signal<DashboardPeriod>({ type: 'month' });
  customDateFrom = signal('');
  customDateTo = signal('');

  readonly formatCurrency = formatDashboardCurrency;

  readonly periodOptions: { value: DashboardPeriodType; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'year', label: 'Año' },
    { value: 'custom', label: 'Personalizado' }
  ];

  readonly periodLabel = computed(() => {
    const p = this.period();
    const { from, to } = getPeriodRange(p);
    return `${from.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })} – ${to.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  });

  readonly todayLabel = computed(() =>
    new Date().toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  );

  readonly summaryLine = computed(() => {
    const d = this.data();
    if (!d || d.loading) return null;
    return {
      issued: d.rates?.totalIssued ?? 0,
      paid: d.rates?.totalPaid ?? 0,
      pending: d.rates?.totalPending ?? 0,
      rate: d.rates?.collectionRate ?? 0
    };
  });

  ngOnInit(): void {
    this.load();
  }

  setPeriod(type: DashboardPeriodType): void {
    if (type === 'custom') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      this.customDateFrom.set(`${y}-${m}-01`);
      const last = new Date(y, now.getMonth() + 1, 0);
      this.customDateTo.set(`${y}-${m}-${String(last.getDate()).padStart(2, '0')}`);
      this.period.set({ type: 'custom' });
      return;
    }
    this.period.set({ type });
    this.load();
  }

  applyCustomPeriod(): void {
    const from = this.customDateFrom();
    const to = this.customDateTo();
    if (!from || !to) return;
    this.period.set({ type: 'custom', dateFrom: from, dateTo: to });
    this.load();
  }

  load(): void {
    this.data.update((d) => {
      const base: DashboardData = d ?? {
        collection: { amount: 0, count: 0 },
        projection: { amount: 0, count: 0 },
        upcomingCharges: [],
        overdueCharges: [],
        recentPayments: [],
        salesByDay: [],
        heatMapData: [],
        rates: { collectionRate: 0, overdueRate: 0, totalIssued: 0, totalPaid: 0, totalPending: 0 },
        invoicesByStatus: {},
        loading: false
      };
      return { ...base, loading: true, error: undefined };
    });
    this.dashboardService.loadDashboard(this.period()).subscribe((d) => this.data.set(d));
  }
}
